import { Target, Eye, ShieldCheck, Scale, Handshake, Lightbulb, Users } from "lucide-react";
import { useSeo } from "../lib/seo";
import { Container, SectionHead, Btn } from "../components/site/Ui";
import { Reveal } from "../components/site/Motion";
import { PageHero, CtaBand } from "../components/site/Blocks";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", text: "We say what we will do, and do what we say, with advertisers and publishers alike." },
  { icon: Scale, title: "Transparency", text: "Clear terms, open communication and reporting that every partner can understand." },
  { icon: Target, title: "Accountability", text: "We focus on the outcomes our partners care about and own our part in delivering them." },
  { icon: Lightbulb, title: "Continuous improvement", text: "We test, learn and refine campaigns so performance can keep improving." },
  { icon: Users, title: "Partnership", text: "Long-term relationships matter more to us than one-off transactions." },
];

export default function About() {
  useSeo("About Us", "Learn about AppTarget Advertising Pvt. Ltd., our mission, vision, core values and partnership philosophy in affiliate and performance marketing.", "/about");
  return (
    <>
      <PageHero eyebrow="About AppTarget" lines={["A performance partner for", "brands and publishers."]} testid="about-hero"
        text="AppTarget Advertising Pvt. Ltd. is an affiliate marketing and performance marketing company. We help advertisers acquire customers through quality traffic partners, and help publishers monetise their audiences through relevant campaigns." />

      <section className="bg-white pb-20 lg:pb-28" data-testid="about-intro">
        <Container className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal className="overflow-hidden rounded-[28px] [clip-path:polygon(0_0,86%_0,100%_14%,100%_100%,0_100%)]">
            <img src="/img/analytics.webp" alt="Campaign analytics on a laptop" loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </Reveal>
          <div>
            <SectionHead eyebrow="Company introduction" title="Built on a simple model: pay for performance." />
            <Reveal delay={0.1} className="mt-6 space-y-4 text-base leading-relaxed text-[#4A5D78] md:text-lg">
              <p>Our business brings together two groups with shared interests. Advertisers want measurable growth from their marketing budgets. Publishers and traffic partners want reliable ways to earn from the audiences they have built.</p>
              <p>We structure campaigns around agreed outcomes such as installs, actions or qualified leads, and manage the partnerships that deliver them, so both sides can work with confidence.</p>
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="bg-[#F4F7FB] py-20 lg:py-28" data-testid="about-mission-vision">
        <Container className="grid gap-6 md:grid-cols-2">
          {[
            { icon: Target, label: "Our Mission", text: "To help businesses grow through transparent, performance-driven marketing, by connecting advertisers with quality traffic partners and managing campaigns that are accountable to real outcomes." },
            { icon: Eye, label: "Our Vision", text: "To be a trusted international performance marketing partner for advertisers and publishers, known for quality partnerships, clear communication and dependable execution." },
          ].map((m, i) => (
            <Reveal key={m.label} delay={i * 0.1} className={`rounded-[28px] p-10 lg:p-14 ${i ? "bg-[#102B50] text-white" : "border border-slate-200 bg-white"}`}>
              <m.icon className={`h-8 w-8 ${i ? "text-[#8EC0FF]" : "text-[#2878E5]"}`} strokeWidth={1.6} />
              <h2 className={`mt-8 font-display text-3xl font-bold ${i ? "text-white" : "text-[#102B50]"}`}>{m.label}</h2>
              <p className={`mt-4 text-base leading-relaxed md:text-lg ${i ? "text-slate-300" : "text-[#4A5D78]"}`}>{m.text}</p>
            </Reveal>
          ))}
        </Container>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="about-values">
        <Container>
          <SectionHead eyebrow="Core values" title="What guides every partnership." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06} className="card-soft group p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F1FD] text-[#2878E5]"><v.icon className="h-5 w-5" /></span>
                <h3 className="mt-6 font-display text-lg font-bold text-[#102B50]">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4A5D78]">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-[#F4F7FB] py-20 lg:py-28" data-testid="about-philosophy">
        <Container className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Partnership philosophy" title="Aligned incentives create better results." />
          </div>
          <div className="space-y-6 lg:col-span-7">
            {[
              ["Shared goals", "When advertisers pay for outcomes and publishers earn on them, everyone is focused on the same result."],
              ["Quality over volume", "We value relevant traffic and genuine engagement over raw numbers that don’t help a business grow."],
              ["Clear terms from day one", "Payouts, KPIs, approved traffic sources and validation rules are agreed before a campaign goes live."],
              ["People you can reach", "Every partner has a point of contact who understands their campaigns and responds."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06} className="flex gap-6 border-b border-slate-200 pb-6">
                <span className="font-display text-sm font-bold text-[#2878E5]">0{i + 1}</span>
                <div>
                  <h3 className="font-display text-xl font-bold text-[#102B50]">{t}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#4A5D78]">{d}</p>
                </div>
              </Reveal>
            ))}
            <Reveal className="pt-4"><Btn to="/contact" data-testid="about-contact-btn"><Handshake className="h-4 w-4" />Talk to Our Team</Btn></Reveal>
          </div>
        </Container>
      </section>
      <CtaBand />
    </>
  );
}
